import dayjs from 'dayjs';
import {
  CreateClassEventReq,
  DeleteEventReq,
  DeleteTypes,
  IntervalType,
  PlaceType,
  RecurrenceTypes,
} from '../services/api.types';
import { getEndHour, isEqualDate, mergeDateAndHour } from './date';
import { EventCalendarValues } from '../components/CalendarEvent';
import { CalendarEvents } from '../components/Calendar';
import { readMoney } from './formatters';
import { TeacherBusyFormValues } from '../components/CalendarEvent/TeacherBusyEventForm';
import {
  RecurrenceDefaultValues,
  RecurrenceValues,
  Weekdays,
  daysArr,
} from './classes.types';

export function buildRecurrence(
  data: EventCalendarValues | TeacherBusyFormValues
): RecurrenceValues {
  const {
    recurrenceType,
    startHour,
    endHour,
    until,
    count,
    weekdays,
    intervalNumber,
    intervalType,
  } = data;

  const startDate = dayjs(
    mergeDateAndHour(data.startDate?.toDate(), startHour?.toDate())
  );

  if (
    recurrenceType === RecurrenceTypes.UNTIL &&
    until &&
    until?.diff(startDate?.startOf('day')) < 0
  )
    throw new Error('Data final menor que a data inicial');

  const duration = (endHour?.diff(startHour) ?? 0) / (60 * 1000);
  if (duration <= 0)
    throw new Error('A hora de término deve ser maior que a hora de início');

  const untilReq =
    recurrenceType === RecurrenceTypes.UNTIL
      ? new Date(until?.toISOString() ?? 0)
      : undefined;

  let recurrenceTypeReq = recurrenceType;
  let countReq =
    recurrenceType === RecurrenceTypes.COUNT ? Number(count) : undefined;

  if (recurrenceType === RecurrenceTypes.ONCE) {
    recurrenceTypeReq = RecurrenceTypes.COUNT;
    countReq = 1;

    const selectDay = Number(startDate?.day());
    weekdays[daysArr[selectDay]] = true;
  }

  return {
    recurrenceRule: {
      recurrenceType: recurrenceTypeReq,
      weekdays: weekdays,
      count: countReq,
      until: untilReq,
      intervalNumber: parseInt(intervalNumber),
      intervalType: intervalType,
    },
    startHour: new Date(startHour?.toISOString() ?? 0),
    duration: Math.round(duration),
    startDate: startDate.toDate(),
    timezone: dayjs.tz.guess(),
  };
}

export async function buildCreateClass(
  data: EventCalendarValues
): Promise<CreateClassEventReq> {
  const {
    recurrenceType,
    startHour,
    endHour,
    until,
    count,
    weekdays,
    startDate,
    student,
    teacher,
    subject,
    intervalNumber,
    intervalType,
    responsible,
    ...newData
  } = data;
  const recurrenceData = buildRecurrence({
    recurrenceType,
    startHour,
    endHour,
    until,
    count,
    weekdays,
    startDate,
    teacher,
    intervalNumber,
    intervalType,
  });

  const createClassEventReq: CreateClassEventReq = {
    ...newData,
    ...recurrenceData,
    teacherId: Number(teacher?.id),
    subjectId: Number(subject?.id),
    studentId: Number(student?.id),
    valueFromResponsible: readMoney(data.valueFromResponsible),
    valueToTeacher: readMoney(data.valueToTeacher),
    valueToTrip: readMoney(data.valueToTrip),
    placeType: newData.placeType ?? PlaceType.PLUS_HEADQUARTER,
  };

  return createClassEventReq;
}

export function buildDeleteClass(
  type: DeleteTypes,
  event: CalendarEvents | null,
  bypassPaymentClass?: boolean
): DeleteEventReq {
  const startDate = mergeDateAndHour(
    new Date(event?.startDate ?? 0),
    new Date(event?.startHour ?? 0)
  );

  const deleteEventReq: DeleteEventReq = {
    date: startDate,
    removeDateRecurrence: type,
    bypassPaymentClass,
  };

  return deleteEventReq;
}

export function shiftWeekdaysToLocalTimezone(
  weekdays: Weekdays,
  startHour: Date
): Weekdays {
  const timezoneStartHour = dayjs(startHour);

  const dayDiff = timezoneStartHour.day() - timezoneStartHour.utc().day();
  if (dayDiff === 0) return weekdays;

  const shiftedWeekdays = daysArr.map((day) => weekdays[day]);
  const shiftRight = dayDiff > 0;

  if (shiftRight) {
    const lastWeekday = shiftedWeekdays.pop() ?? false;
    shiftedWeekdays.unshift(lastWeekday);
  } else {
    const firstWeekday = shiftedWeekdays.shift() ?? false;
    shiftedWeekdays.push(firstWeekday);
  }

  const formattedWeekdays = { ...weekdays };
  daysArr.forEach((day, index) => {
    formattedWeekdays[day] = shiftedWeekdays[index];
  });

  return formattedWeekdays;
}

export function buildRecurrenceDefaultValues(
  recurrenceType: RecurrenceTypes,
  event: CalendarEvents | null
): RecurrenceDefaultValues {
  const {
    startDate: defaultStartDate,
    startHour: defaultStartHour,
    endHour: defaultEndHour,
  } = getDefaultNowDates();

  const count = parseInt(getPropFromRRuleString(event?.rrule, 'COUNT', '1'));
  const days = getPropFromRRuleString(event?.rrule, 'BYDAY', '');
  const intervalNumber = parseInt(
    getPropFromRRuleString(event?.rrule, 'INTERVAL', '1')
  );
  const intervalType = getPropFromRRuleString(
    event?.rrule,
    'FREQ',
    IntervalType.WEEKLY
  ) as IntervalType;

  const endHour =
    event?.startHour && event?.duration
      ? getEndHour(event.startHour, event.duration)
      : defaultEndHour;

  const eventCalendarValues: RecurrenceDefaultValues = {
    startHour: event?.startHour ? dayjs(event?.startHour) : defaultStartHour,
    endHour: dayjs(endHour),
    startDate: event?.startDate ? dayjs(event?.startDate) : defaultStartDate,
    weekdays: {
      monday: days.includes('MO'),
      tuesday: days.includes('TU'),
      wednesday: days.includes('WE'),
      thursday: days.includes('TH'),
      friday: days.includes('FR'),
      saturday: days.includes('SA'),
      sunday: days.includes('SU'),
    },
    intervalType: intervalType,
    intervalNumber: intervalNumber.toString(),
    recurrenceType: recurrenceType,
    until:
      recurrenceType === RecurrenceTypes.UNTIL ? dayjs(event?.endDate) : null,
    count: count,
  };

  eventCalendarValues.weekdays = shiftWeekdaysToLocalTimezone(
    eventCalendarValues.weekdays,
    eventCalendarValues.startHour?.toDate() ?? new Date()
  );

  return eventCalendarValues;
}

export function isSameRecurrence(
  originalEvent: CalendarEvents | null,
  formValues: EventCalendarValues | TeacherBusyFormValues,
  ignoreHour = false
): boolean {
  const recurrenceOriginalValues = buildRecurrenceDefaultValues(
    getRecurrenceTypeFromEvent(originalEvent),
    originalEvent
  );

  const isUntilEqual =
    recurrenceOriginalValues.until === formValues.until ||
    isEqualDate(recurrenceOriginalValues.until, formValues.until);

  const isHoursEqual =
    ignoreHour ||
    (isEqualDate(recurrenceOriginalValues.startHour, formValues.startHour) &&
      isEqualDate(recurrenceOriginalValues.endHour, formValues.endHour));

  if (
    recurrenceOriginalValues.recurrenceType === formValues.recurrenceType &&
    recurrenceOriginalValues.count === formValues.count &&
    recurrenceOriginalValues.intervalNumber === formValues.intervalNumber &&
    recurrenceOriginalValues.intervalType === formValues.intervalType &&
    isWeekdaysEqual(recurrenceOriginalValues.weekdays, formValues.weekdays) &&
    isUntilEqual &&
    isHoursEqual
  ) {
    return true;
  }

  return false;
}

export function getRecurrenceTypeFromEvent(event?: CalendarEvents | null) {
  if (!event) return RecurrenceTypes.ONCE;

  const neverDate = dayjs('2050-01-01T00:00:00.000Z');
  if (Math.abs(dayjs(event.endDate).diff(neverDate, 'days')) < 7)
    return RecurrenceTypes.NEVER;

  if (!event.rrule || event.rrule.includes('COUNT=1;'))
    return RecurrenceTypes.ONCE;

  if (event.rrule.includes('COUNT=')) return RecurrenceTypes.COUNT;

  return RecurrenceTypes.UNTIL;
}

const isWeekdaysEqual = (w1: Weekdays, w2: Weekdays) => {
  return (
    w1.monday === w2.monday &&
    w1.tuesday === w2.tuesday &&
    w1.wednesday === w2.wednesday &&
    w1.thursday === w2.thursday &&
    w1.friday === w2.friday &&
    w1.saturday === w2.saturday &&
    w1.sunday === w2.sunday
  );
};

const getPropFromRRuleString = (
  rrule: string | undefined,
  prop: string,
  defaultValue: string
): string => {
  if (!rrule) return defaultValue;

  const propRegex = new RegExp(`${prop}=(.*?)(;|$|\n)`, 'g');
  const propMatch = propRegex.exec(rrule);
  return propMatch?.[1] ?? defaultValue;
};

const getDefaultNowDates = () => {
  const nowHalf = dayjs().set('minutes', 30).startOf('minute');
  const now = nowHalf.isBefore(dayjs()) ? nowHalf.add(30, 'minutes') : nowHalf;

  const endHour = now.add(1, 'hour');
  const endDay = now.endOf('day').startOf('minute');

  return {
    startDate: now,
    startHour: now,
    endHour: endHour.isBefore(endDay) ? endHour : endDay,
  };
};
